/**
 * Реестр и панель статусов работ (задача 114 §2.1/§2.2).
 *
 * `unitId` входит в ключ кеша реестра: у admin реестр всегда привязан к
 * выбранному подразделению, и без юнита в ключе переключение селектора отдавало
 * бы данные прошлого юнита.
 *
 * **Автосохранение панели.** Явной кнопки «Сохранить» нет: каждая мутация
 * уходит сразу и возвращает свежую деталь, которую кладём в кеш напрямую —
 * панель не мигает лишним рефетчем. Реестр после этого инвалидируется: там
 * меняется не только чип статуса, но и производные часы доработки, которые
 * считаются на чтении от даты закрытия.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import {
  addCheckpoint,
  addCheckpointByPair,
  addWorkComment,
  deleteCheckpoint,
  getWorkDetail,
  getWorkStatusRegistry,
  patchWork,
  toggleCatalogCheckpoint,
  toggleCheckpoint,
  type WorkPair,
  type WorkPatchBody,
  type WorkStatusDetail,
  type WorkStatusRegistry,
} from "@/api/workStatus";

export const WORK_STATUS_KEY = ["work-status", "registry"] as const;
export const WORK_DETAIL_KEY = ["work-status", "detail"] as const;

function noRetryOn401(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && error.status === 401) return false;
  return failureCount < 1;
}

export function useWorkStatusRegistry(
  unitId: number | null,
  includeChildren = false,
  enabled = true,
) {
  return useQuery<WorkStatusRegistry, Error>({
    queryKey: [...WORK_STATUS_KEY, { unitId, includeChildren }] as const,
    queryFn: () => getWorkStatusRegistry(unitId, includeChildren),
    enabled,
    retry: noRetryOn401,
    staleTime: 30_000,
  });
}

export function useWorkDetail(
  pair: WorkPair | null,
  unitId: number | null = null,
  includeChildren = false,
) {
  return useQuery<WorkStatusDetail, Error>({
    // Юнит — часть ключа: у admin деталь привязана к выбранному подразделению
    // ровно так же, как реестр.
    queryKey: [...WORK_DETAIL_KEY, pair, { unitId, includeChildren }] as const,
    queryFn: () => getWorkDetail(pair as WorkPair, unitId, includeChildren),
    enabled: pair != null,
    retry: noRetryOn401,
  });
}

/** Свежая деталь — в кеш; реестр — под инвалидацию (чип, точки, доработка). */
function settle(qc: QueryClient, pair: WorkPair, detail: WorkStatusDetail): void {
  // Ключ детали несёт юнит — точечная подстановка по префиксу, иначе свежий
  // ответ лёг бы мимо кеша открытой панели.
  qc.setQueriesData({ queryKey: [...WORK_DETAIL_KEY, pair] }, detail);
  void qc.invalidateQueries({ queryKey: WORK_STATUS_KEY });
}

/**
 * Патч работы. Статус и прогноз подменяем оптимистично, чтобы контрол не
 * «отскакивал» на время запроса; при ошибке откатываемся на снимок — ошибку
 * показывает вызывающий у поля (валидация даты закрытия §1.3 приходит 422 с
 * русским detail, глобальным тостом её показывать нельзя).
 */
export function usePatchWork(pair: WorkPair | null) {
  const qc = useQueryClient();
  // Префикс, а не точный ключ: в ключе детали есть ещё юнит (у admin), и
  // точечный `getQueryData` промахнулся бы мимо открытой панели.
  const key = [...WORK_DETAIL_KEY, pair] as const;
  return useMutation<WorkStatusDetail, Error, WorkPatchBody, { previous?: WorkStatusDetail }>({
    mutationFn: (body) => patchWork(pair as WorkPair, body),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueriesData<WorkStatusDetail>({ queryKey: key })[0]?.[1];
      if (previous) {
        qc.setQueriesData<WorkStatusDetail>(
          { queryKey: key },
          {
            ...previous,
            ...(body.status !== undefined ? { status: body.status } : {}),
            ...(body.forecast !== undefined ? { forecast: body.forecast } : {}),
            ...(body.forecast_cleared ? { forecast: null } : {}),
          },
        );
      }
      return { previous };
    },
    onError: (_err, _body, ctx) => {
      if (ctx?.previous) qc.setQueriesData({ queryKey: key }, ctx.previous);
    },
    onSuccess: (detail) => {
      if (pair) settle(qc, pair, detail);
    },
  });
}

/**
 * Мутации точек и комментария; ответ у всех — свежая деталь.
 *
 * **Работы может ещё не быть.** До первого действия overlay не создан, поэтому
 * у каждой мутации две адресации: по id (работа заведена) и по паре (нет).
 * Выбирает её хук, а не компонент — панель просто говорит, что сделал
 * пользователь. Парный вариант заводит работу тем же запросом: первым
 * действием считается любое осмысленное, мёртвых кликов в панели нет
 * (решение владельца 20.07.2026).
 */
export function useWorkCheckpointMutations(pair: WorkPair | null) {
  const qc = useQueryClient();
  const onDone = (detail: WorkStatusDetail): void => {
    if (pair) settle(qc, pair, detail);
  };

  const add = useMutation<WorkStatusDetail, Error, { workId: number | null; title: string }>({
    mutationFn: ({ workId, title }) =>
      workId == null ? addCheckpointByPair(pair as WorkPair, title) : addCheckpoint(workId, title),
    onSuccess: onDone,
  });
  const toggle = useMutation<
    WorkStatusDetail,
    Error,
    { itemId: number | null; catalogItemId: number | null; isDone: boolean }
  >({
    // У точки-инстанса есть свой id; у превью каталога его нет — там адресуем
    // пунктом каталога, и сервер заводит работу вместе с отметкой.
    mutationFn: ({ itemId, catalogItemId, isDone }) =>
      itemId == null
        ? toggleCatalogCheckpoint(pair as WorkPair, catalogItemId as number, isDone)
        : toggleCheckpoint(itemId, isDone),
    onSuccess: onDone,
  });
  const remove = useMutation<WorkStatusDetail, Error, { itemId: number }>({
    mutationFn: ({ itemId }) => deleteCheckpoint(itemId),
    onSuccess: onDone,
  });
  const comment = useMutation<WorkStatusDetail, Error, { workId: number | null; memo: string }>({
    // Комментарий до заведения работы — обычный патч с одним `memo`: сервер
    // создаёт overlay и пишет standalone-событие журнала.
    mutationFn: ({ workId, memo }) =>
      workId == null ? patchWork(pair as WorkPair, { memo }) : addWorkComment(workId, memo),
    onSuccess: onDone,
  });

  return { add, toggle, remove, comment };
}
